import { gql } from '@apollo/client';
import { forPaginated } from './common/paginated.graphql';
import { CreateProductTransactionInput, ProductTransaction } from '../types/productTransaction';
import { CustomFieldFragment } from './customField.graphql';
import { UserFragment } from './user.graphql';

const productTransaction = gql`
  fragment ProductTransaction on ProductTransaction {
    id
    version
    companyId
    type
    createdAt
    createdBy
    updatedAt
    updatedBy
    processedBy
    processedAt
    pickedAt
    pickedBy
    parentId
    parentType
    status
    processType
    containerTransactionId
    kitTransactionId
    customFields {
      ...CustomFieldValue
    }
    externalIdentifier {
      settingsId
      externalId
      type
    }
    product {
      fromStockLocationId
      toStockLocationId
      toProductId
      fromProductId
      toContainerId
      fromContainerId
      lotId
      pmdId
      serialNbr
      lpn
      toBinId
      fromBinId
      quantity
      containerId
      processedQuantity
      containerComplete
      fromCustomFields {
        ...CustomFieldValue
      }
      toCustomFields {
        ...CustomFieldValue
      }
      kit {
        templateId
        complete
        fromKitId
        toKitId
      }
    }
    reasonForFailure
    externalIdentifier {
      externalId
      settingsId
      type
      source
    }
    metadata {
      fromContainer {
        id
        identifier
      }
      toContainer {
        id
        identifier
      }
      user {
        ...User
      }
      toKit {
        identifier
      }
      fromKit {
        identifier
      }
      kitTemplate {
        name
      }
      lot {
        id
        companyId
        productMasterDataId
        number
        expirationDate
      }
      fromBin {
        id
        companyId
        stockLocationId
        binStatusId
        name
      }
      toBin {
        id
        companyId
        stockLocationId
        binStatusId
        name
      }
      productMasterData {
        id
        companyId
        productName
        productNumber
        unitOfMeasure {
          system
          unit
          unitType
        }
      }
      assignedUser {
        ...User
      }
    }
  }
  ${CustomFieldFragment.customFieldValue}
  ${UserFragment.user}
`;

const get = gql`
  query ProductTransactions($companyId: String!, $page: Float, $batchSize: Float, $since: Date) {
    productTransactions(companyId: $companyId, page: $page, batchSize: $batchSize, since: $since) {
      data {
        ...ProductTransaction
      }
      page
      pageSize
      batchSize
      hasNext
    }
  }
  ${productTransaction}
`;

export interface GetProductTransactionsVariables {
  companyId: string;
  page?: number;
  batchSize?: number;
  since?: Date;
}

export interface GetProductTransactionsResponse {
  productTransactions: PaginatedProductTransactions;
}

class PaginatedProductTransactions extends forPaginated<ProductTransaction>() {}

const createAndProcess = gql`
  mutation CreateAndProcessTransaction($productTransactions: [CreateProductTransactionInput!]!) {
    createAndProcessTransaction(productTransactions: $productTransactions) {
      ...ProductTransaction
    }
  }
  ${productTransaction}
`;

export interface CreateAndProcessProductTransactionVariables {
  productTransactions: CreateProductTransactionInput[];
}

export interface CreateAndProcessProductTransactionResponse {
  createAndProcessTransaction: ProductTransaction[];
}

const process = gql`
  mutation ProcessProductTransaction($productTransactions: [ProcessProductTransactionInput!]!) {
    processProductTransaction(productTransactions: $productTransactions) {
      ...ProductTransaction
    }
  }
  ${productTransaction}
`;

export interface ProcessProductTransactionsVariables {
  productTransactions: ProductTransaction[];
}

export interface ProcessProductTransactionsResponse {
  processProductTransaction: ProductTransaction[];
}

const updateCustomFields = gql`
  mutation UpdateProductTransactionCustomFields($productTransactions: [UpdateProductTransactionInput!]!) {
    updateProductTransactionCustomFields(productTransactions: $productTransactions) {
      ...ProductTransaction
    }
  }
  ${productTransaction}
`;

export interface UpdateProductTransactionCustomFieldsVariables {
  productTransactions: ProductTransaction[];
}

export interface UpdateProductTransactionCustomFieldsResponse {
  updateProductTransactionCustomFields: ProductTransaction[];
}

const updateProductTransaction = gql`
  mutation UpdateProductTransaction($productTransactions: [UpdateProductTransactionInput!]!) {
    updateProductTransaction(productTransactions: $productTransactions) {
      ...ProductTransaction
    }
  }
  ${productTransaction}
`;

export interface UpdateProductTransactionVariables {
  productTransactions: ProductTransaction[];
}

export interface UpdateProductTransactionVariablesResponse {
  updateProductTransaction: ProductTransaction[];
}

const rollback = gql`
  mutation RollbackProductTransaction($productTransactions: [RollbackProductTransactionInput!]!) {
    rollbackProductTransaction(productTransactions: $productTransactions) {
      ...ProductTransaction
    }
  }
  ${productTransaction}
`;

export interface RollbackProductTransactionsVariables {
  productTransactions: ProductTransaction[];
}

export interface RollbackProductTransactionsResponse {
  rollbackProductTransaction: ProductTransaction[];
}

const cancel = gql`
  mutation CancelProductTransaction($ids: [String!]!) {
    cancelProductTransaction(ids: $ids) {
      ...ProductTransaction
    }
  }
  ${productTransaction}
`;

export interface CancelProductTransactionVariables {
  ids: string[];
}

export interface CancelProductTransactionResponse {
  cancelProductTransaction: ProductTransaction[];
}

const processAsset = gql`
  mutation ProcessProductTransactionForAsset($productTransaction: ProcessProductTransactionInput!, $userId: String) {
    processProductTransactionForAsset(productTransaction: $productTransaction, userId: $userId) {
      ...ProductTransaction
    }
  }
  ${productTransaction}
`;

export interface ProcessProductTransactionForAssetVariables {
  productTransaction: ProductTransaction;
  userId?: string;
}

export interface ProcessProductTransactionForAssetResponse {
  processProductTransactionForAsset: ProductTransaction;
}

export const ProductTransactionQueries = {
  get,
};

export const ProductTransactionMutations = {
  createAndProcess,
  process,
  updateCustomFields,
  rollback,
  processAsset,
  cancel,
  updateProductTransaction,
};

export const ProductTransactionFragments = {
  productTransaction,
};
